import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import type { Carrier, CarrierPriceList, CarrierPriceListFileContentType } from '@magimp/shared';
import { Button, Input } from '../../../ui/index.js';
import {
  createCarrierPriceList,
  deleteCarrierPriceList,
  patchCarrierPriceList,
  requestPriceListImageDownloadUrl,
  requestUploadUrl,
  type CarrierPriceListBody,
} from '../../../api/requests/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { Card, CardRow, FieldGrid, Labeled, SectionTitle } from './CarrierEditorModal.parts.js';

const ACCEPT_LIST: CarrierPriceListFileContentType[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
];
const ACCEPT_HEADER = ACCEPT_LIST.join(',');

type DraftPriceList = {
  name: string;
  imageObjectKey: string;
  fileContentType?: CarrierPriceListFileContentType;
};

const EMPTY_PRICE_LIST_DRAFT: DraftPriceList = { name: '', imageObjectKey: '' };

export function CarrierPriceListSection({
  carrier,
  onRefresh,
  onDirtyChange,
}: {
  carrier: Carrier;
  onRefresh: () => Promise<void>;
  // INVARIANT: same narrow primitive contract as CarrierWarehouseSection.
  // The parent editor aggregates this into one `useUnsavedChangesGuard`.
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation(['carriers', 'common']);
  const showToast = useToastStore((s) => s.show);
  const [draft, setDraft] = useState<DraftPriceList>(EMPTY_PRICE_LIST_DRAFT);
  const [rowDirtyIds, setRowDirtyIds] = useState<ReadonlySet<string>>(() => new Set());
  const create = useMutation({
    mutationFn: () =>
      createCarrierPriceList(carrier.id, {
        name: draft.name,
        imageObjectKey: draft.imageObjectKey,
        // INVARIANT: new uploads always carry the selected File.type as the
        // wire-side discriminator. Sending undefined would silently mark
        // the row as a legacy image; the section refuses to create without
        // an uploaded file so this property is always populated.
        ...(draft.fileContentType ? { fileContentType: draft.fileContentType } : {}),
      } satisfies CarrierPriceListBody),
    onSuccess: async () => {
      setDraft(EMPTY_PRICE_LIST_DRAFT);
      await onRefresh();
      showToast({ tone: 'success', text: t('toasts.priceListCreated') });
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  // INVARIANT: an already-uploaded-but-never-saved object key counts as
  // dirty per Decision 27 — the local draft is a divergence from the
  // empty baseline even if the form name field is still empty. The
  // unsaved-upload object remains orphaned on the backend after a
  // discard close; cleanup is explicitly out of scope (issue #628).
  const draftDirty = isPriceListDraftDirty(draft);
  const dirty = draftDirty || rowDirtyIds.size > 0;
  // WHY: ref-stable callback so the dirty-effect depends only on `dirty`
  // and the unmount cleanup runs exactly once.
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  }, [onDirtyChange]);
  useEffect(() => {
    onDirtyChangeRef.current?.(dirty);
  }, [dirty]);
  useEffect(
    () => () => {
      onDirtyChangeRef.current?.(false);
    },
    [],
  );

  const setRowDirty = (id: string, isDirty: boolean): void => {
    setRowDirtyIds((prev) => {
      const has = prev.has(id);
      if (isDirty === has) return prev;
      const next = new Set(prev);
      if (isDirty) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const canCreate =
    draft.name.trim().length > 0 && draft.imageObjectKey.length > 0 && !create.isPending;

  return (
    <section>
      <SectionTitle>{t('sections.priceLists')}</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {carrier.priceLists.map((row) => (
          <PriceListRow
            key={row.id}
            carrierId={carrier.id}
            row={row}
            onRefresh={onRefresh}
            onDirtyChange={(d) => setRowDirty(row.id, d)}
          />
        ))}
        <Card>
          <FieldGrid>
            <Labeled label={t('fields.priceListName')} required>
              <Input
                value={draft.name}
                onChange={(name) => setDraft({ ...draft, name })}
                placeholder={t('fields.priceListName')}
              />
            </Labeled>
            <Labeled label={t('fields.file')} required>
              <FileUploadButton
                attached={draft.imageObjectKey.length > 0}
                onUploaded={(objectKey, fileContentType) =>
                  setDraft({ ...draft, imageObjectKey: objectKey, fileContentType })
                }
              />
            </Labeled>
          </FieldGrid>
          <CardRow>
            <Button icon="plus" disabled={!canCreate} onClick={() => create.mutate()}>
              {t('actions.addPriceList')}
            </Button>
          </CardRow>
        </Card>
      </div>
    </section>
  );
}

function PriceListRow({
  carrierId,
  row,
  onRefresh,
  onDirtyChange,
}: {
  carrierId: string;
  row: CarrierPriceList;
  onRefresh: () => Promise<void>;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation(['carriers', 'common']);
  const showToast = useToastStore((s) => s.show);
  // INVARIANT: baseline resyncs from `row.*` props so after a successful
  // save/refresh the parent re-renders us and dirty drops back to false.
  const [form, setForm] = useState<CarrierPriceListBody>({
    name: row.name,
    imageObjectKey: row.imageObjectKey,
    ...(row.fileContentType ? { fileContentType: row.fileContentType } : {}),
  });
  useEffect(() => {
    setForm({
      name: row.name,
      imageObjectKey: row.imageObjectKey,
      ...(row.fileContentType ? { fileContentType: row.fileContentType } : {}),
    });
  }, [row.name, row.imageObjectKey, row.fileContentType]);

  const save = useMutation({
    mutationFn: () => patchCarrierPriceList(carrierId, row.id, form),
    onSuccess: async () => {
      await onRefresh();
      showToast({ tone: 'success', text: t('toasts.priceListUpdated') });
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });
  const remove = useMutation({
    mutationFn: () => deleteCarrierPriceList(carrierId, row.id),
    onSuccess: async () => {
      await onRefresh();
      showToast({ tone: 'success', text: t('toasts.priceListDeleted') });
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const dirty =
    form.name !== row.name ||
    form.imageObjectKey !== row.imageObjectKey ||
    (form.fileContentType ?? null) !== (row.fileContentType ?? null);
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  }, [onDirtyChange]);
  useEffect(() => {
    onDirtyChangeRef.current?.(dirty);
  }, [dirty]);
  useEffect(
    () => () => {
      onDirtyChangeRef.current?.(false);
    },
    [],
  );

  async function openFile(): Promise<void> {
    try {
      const signed = await requestPriceListImageDownloadUrl(row.id);
      window.open(signed.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      showToast({ tone: 'error', text: errorMessage(err) });
    }
  }

  const canSave = form.name.trim().length > 0 && form.imageObjectKey.length > 0 && !save.isPending;

  return (
    <Card>
      <FieldGrid>
        <Labeled label={t('fields.priceListName')} required>
          <Input value={form.name} onChange={(name) => setForm({ ...form, name })} />
        </Labeled>
        <Labeled label={t('fields.file')} required>
          <FileUploadButton
            attached={form.imageObjectKey.length > 0}
            onUploaded={(imageObjectKey, fileContentType) =>
              // INVARIANT: when replacing the object, always send the new
              // content type alongside the key so the row's discriminator
              // stays consistent with the stored file (issue #586).
              setForm({
                ...form,
                imageObjectKey,
                ...(fileContentType ? { fileContentType } : {}),
              })
            }
          />
        </Labeled>
      </FieldGrid>
      <CardRow>
        <Button size="sm" kind="ghost" icon="external-link" onClick={() => void openFile()}>
          {t('actions.open')}
        </Button>
        <Button size="sm" kind="danger" onClick={() => remove.mutate()}>
          {t('common:actions.delete')}
        </Button>
        <Button size="sm" disabled={!canSave} onClick={() => save.mutate()}>
          {t('common:actions.save')}
        </Button>
      </CardRow>
    </Card>
  );
}

function FileUploadButton({
  attached,
  onUploaded,
}: {
  attached: boolean;
  onUploaded: (objectKey: string, fileContentType: CarrierPriceListFileContentType) => void;
}) {
  const { t } = useTranslation(['carriers', 'common']);
  const showToast = useToastStore((s) => s.show);
  const [uploading, setUploading] = useState(false);

  async function onFile(file: File): Promise<void> {
    // INVARIANT: closed-set client-side gate mirrors the backend's
    // carrier-price-list scope policy. Backend still re-validates, so
    // this is a UX shortcut, not the source of truth.
    if (!isSupportedContentType(file.type)) {
      showToast({ tone: 'error', text: t('errors.imageType') });
      return;
    }
    const contentType = file.type;
    setUploading(true);
    try {
      const signed = await requestUploadUrl({
        contentType,
        contentLength: file.size,
        scope: 'carrier-price-list',
      });
      const res = await fetch(signed.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'content-type': contentType },
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status}`);
      onUploaded(signed.objectKey, contentType);
      showToast({ tone: 'success', text: t('toasts.fileUploaded') });
    } catch (err) {
      showToast({ tone: 'error', text: errorMessage(err) });
    } finally {
      setUploading(false);
    }
  }

  const label = uploading
    ? t('actions.uploading')
    : attached
      ? t('actions.replaceFile')
      : t('actions.attachFile');

  return (
    <label
      style={{
        height: 'var(--control-h)',
        padding: '0 10px',
        borderRadius: 'var(--r-2)',
        border: '1px solid var(--border-1)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: uploading ? 'wait' : 'pointer',
        fontSize: 13,
        background: attached ? 'var(--bg-2)' : 'var(--bg-1)',
        minWidth: 0,
      }}
    >
      {label}
      <input
        type="file"
        accept={ACCEPT_HEADER}
        disabled={uploading}
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          e.currentTarget.value = '';
          if (file) void onFile(file);
        }}
        style={{ display: 'none' }}
      />
    </label>
  );
}

function isSupportedContentType(value: string): value is CarrierPriceListFileContentType {
  return (ACCEPT_LIST as readonly string[]).includes(value);
}

// WHY: a typed-but-not-yet-saved row counts as dirty. An already-uploaded
// object key (no name yet) also counts: the user attached a file they did
// not save, and per Decision 27 we keep the prompt rather than orphan the
// upload silently. Backend orphan cleanup is a separate follow-up.
function isPriceListDraftDirty(draft: DraftPriceList): boolean {
  return draft.name.trim().length > 0 || draft.imageObjectKey.length > 0;
}
